import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { type UseMutateFunction } from '@tanstack/react-query';

import { useGetCarValuation } from './queries/hooks/CarValuation.queries';
import { useBrowserStorage } from '../../../../../hooks/UseBrowserStorage';

import {
  CAR_VALUATION_TOOL_SESSION_STORAGE_DATA_KEY,
  ON_SIGN_IN_KEY,
  ON_SIGN_IN_VALUE,
  REGISTRATION_NUMBER_KEY,
} from './CarValuationTool.constants';

import { type PrivateValuation } from './queries/schema/PrivateValuationSchema';

type CarValuationToolProps = {
  children: ReactNode;
};

type CarValuationToolContext = {
  registrationNumber: string;
  setRegistrationNumber: (t: string) => void;
  isValueCarModalOpen: boolean;
  setIsValueCarModalOpen: (t: boolean) => void;
  removeCarValuationToolSessionStorageData: () => void;
  sessionStorageSigninKey: string | undefined;
  sessionStorageRegistrationNumber: string | undefined;
  setCarValuationToolSessionStorageData: (registrationNumber: string) => void;
  resetCarValuationForm: () => void;
  carValuationData?: PrivateValuation;
  isCarValuationPending: boolean;
  carValuationError: Error | null;
  requestCarValuation: UseMutateFunction<
    PrivateValuation,
    Error,
    {
      registrationNumber: string;
      mileageInKilometers: string;
    }
  >;
  closeValueCarModal: () => void;
  isRemoveUserVehicleModalOpen: boolean;
  setIsRemoveUserVehicleModalOpen: (t: boolean) => void;
  isValuationArtificiallyLoading: boolean;
  setIsValuationArtificiallyLoading: (isLoading: boolean) => void;
};

const CarValuationToolContext = createContext<CarValuationToolContext | null>(
  null,
);

const CarValuationToolProvider = ({ children }: CarValuationToolProps) => {
  /**
   * The car valuation modal should be accessible from multiple components,
   * specifically the on-page component used to get a car valuation, where they may access the modal after providing a registration number,
   * and from the component used to display the details of a vehicle associated with their account, if they wish to update that vehicle.
   *
   * Similarly, the value for an inputted registration is shared between that on-page car valuation component and the car valuation modal.
   *
   * Using context allows us to easily share the functionality to read and write this state, along with session storage data,
   * as opposed to passing these values and functions as props, or defining and redefining local state.
   */
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [isValueCarModalOpen, setIsValueCarModalOpen] = useState(false);
  const [isRemoveUserVehicleModalOpen, setIsRemoveUserVehicleModalOpen] =
    useState(false);
  const [isValuationArtificiallyLoading, setIsValuationArtificiallyLoading] =
    useState(false);

  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  /*
   * If an unauthenticated user enters a registration number, we do not open the car valuaton modal with this input.
   * Instead, we record that input and the action to take when they are authenticated in session storage, before directing them to the signin page.
   *
   * Once they have returned, we can read this data to determine what action to take, based on whether they have been authenticated
   * and whether they already have a user vehicle, determined elsewhere.
   *
   * If they are authenticated with no vehicle associated with their account, we open the modal with the value they previously provided,
   * before we directed them to the signin page.
   *
   * If they do have a vehicle associated with their account, we display the details of that vehicle and do not open the modal.
   *
   * In any case, once the data has been read it is removed from session storage.
   */
  const getCarValuationToolSessionStorageData = () => {
    const sessionStorageData = getSessionStorageItem(
      CAR_VALUATION_TOOL_SESSION_STORAGE_DATA_KEY,
    );
    const sessionStorageSignInKey = sessionStorageData?.[ON_SIGN_IN_KEY];
    const sessionStorageRegistrationNumber =
      sessionStorageData?.[REGISTRATION_NUMBER_KEY];

    if (
      typeof sessionStorageSignInKey === 'string' &&
      typeof sessionStorageRegistrationNumber === 'string'
    ) {
      return {
        [ON_SIGN_IN_KEY]: sessionStorageSignInKey,
        [REGISTRATION_NUMBER_KEY]: sessionStorageRegistrationNumber,
      };
    }
    return null;
  };

  const setCarValuationToolSessionStorageData = useCallback(
    (registrationNumber: string) => {
      setSessionStorageItem(CAR_VALUATION_TOOL_SESSION_STORAGE_DATA_KEY, {
        [ON_SIGN_IN_KEY]: ON_SIGN_IN_VALUE,
        [REGISTRATION_NUMBER_KEY]: registrationNumber,
      });
    },
    [setSessionStorageItem],
  );

  const removeCarValuationToolSessionStorageData = useCallback(() => {
    removeSessionStorageItem(CAR_VALUATION_TOOL_SESSION_STORAGE_DATA_KEY);
  }, [removeSessionStorageItem]);

  const sessionStorageData = getCarValuationToolSessionStorageData();
  const sessionStorageSigninKey = sessionStorageData?.[ON_SIGN_IN_KEY];
  const sessionStorageRegistrationNumber =
    sessionStorageData?.[REGISTRATION_NUMBER_KEY];

  const {
    data: carValuationData,
    isPending: isCarValuationPending,
    error: carValuationError,
    mutate: requestCarValuation,
    reset: resetCarValuation,
  } = useGetCarValuation();

  const resetCarValuationForm = useCallback(() => {
    setRegistrationNumber('');
    resetCarValuation();
  }, [resetCarValuation]);

  const closeValueCarModal = useCallback(() => {
    setIsValueCarModalOpen(false);
    /**
     * On close, we clear the data in the modal
     */
    resetCarValuationForm();
    /**
     * Disable the loading state in the event that the user closes the modal
     * before the loading animation has finished.
     */
    setIsValuationArtificiallyLoading(false);
  }, [resetCarValuationForm]);

  const contextValue = useMemo(
    () => ({
      registrationNumber,
      setRegistrationNumber,
      isValueCarModalOpen,
      setIsValueCarModalOpen,
      removeCarValuationToolSessionStorageData,
      setCarValuationToolSessionStorageData,
      sessionStorageSigninKey,
      sessionStorageRegistrationNumber,
      isRemoveUserVehicleModalOpen,
      setIsRemoveUserVehicleModalOpen,
      carValuationData,
      isCarValuationPending,
      carValuationError,
      requestCarValuation,
      resetCarValuationForm,
      closeValueCarModal,
      isValuationArtificiallyLoading,
      setIsValuationArtificiallyLoading,
    }),
    [
      registrationNumber,
      isValueCarModalOpen,
      setRegistrationNumber,
      setIsValueCarModalOpen,
      removeCarValuationToolSessionStorageData,
      setCarValuationToolSessionStorageData,
      isRemoveUserVehicleModalOpen,
      setIsRemoveUserVehicleModalOpen,
      sessionStorageSigninKey,
      sessionStorageRegistrationNumber,
      carValuationData,
      isCarValuationPending,
      carValuationError,
      requestCarValuation,
      resetCarValuationForm,
      closeValueCarModal,
      isValuationArtificiallyLoading,
      setIsValuationArtificiallyLoading,
    ],
  );

  return (
    <CarValuationToolContext.Provider value={contextValue}>
      {children}
    </CarValuationToolContext.Provider>
  );
};

const useCarValuationToolContext = () => {
  const context = useContext(CarValuationToolContext);
  if (!context) {
    throw new Error(
      'useCarValuationToolContext must be used within a CarValuationTool Provider',
    );
  }
  return context;
};

export { CarValuationToolProvider, useCarValuationToolContext };
